import { supabase } from '../supabaseClient';
import { ladeStilleProjekte } from '../projectQuietService';
import { ladeUebungsProjektIds } from '../uebung/uebungService';
import { describeError } from '../errorMessage';
import type { IntegrityFinding, IntegritySeverity, IntegrityScopeKind } from './integrityTypes';

/**
 * Datenbankzugriff fuer den Integritaetswaechter (Tabelle
 * `public.integrity_findings` und RPC `public.acknowledge_integrity_finding`
 * — supabase/integrity_findings.sql, MIGRATIONS.md Nr. 56). Die einzige
 * Stelle, die mit beiden redet.
 *
 * Die REGELN liegen daneben in `integrityLogic.ts` und sind ohne Datenbank
 * pruefbar; hier steht nur, was ohne Supabase nicht zu haben ist. Befunde
 * ENTSTEHEN hier nicht — die schreibt der Nachtlauf mit `service_role`. Fuer
 * den Client gibt es genau einen Schreibweg, und der ist die Quittierung.
 *
 * Hausregel wie im uebrigen Repo: LESEN IST FAIL-OPEN, SCHREIBEN NICHT.
 * Fehlt die Tabelle (Migration Nr. 56 noch nicht gefahren), liefert das Lesen
 * eine leere Liste statt zu werfen — der Reiter zeigt dann eben nichts, statt
 * die Verwaltung mitzureissen. Die Quittierung meldet jeden Fehler dagegen
 * sichtbar: still zu scheitern waere hier besonders teuer, weil der Nutzer
 * sonst glaubt, den Befund abgehakt zu haben, waehrend er offen bleibt —
 * genau die Klasse stiller Nicht-Wirkung, gegen die dieser Waechter gebaut
 * wurde.
 */

/**
 * Der Befund, wie er in der Tabelle STEHT: die Regel-Daten aus §1 plus das
 * Gedaechtnis, das die Tabelle drumherum fuehrt (Erstsichtung, letzte
 * Sichtung, Erledigung, Quittierung).
 *
 * `IntegrityFinding` beschreibt bewusst nur, was die reine Regelmaschine
 * erzeugen kann — sie kennt weder `id` noch „seit wann". Deshalb steht die
 * Erweiterung hier und nicht in `integrityTypes.ts`.
 */
export interface StoredIntegrityFinding extends IntegrityFinding {
    id: string;
    /** Erstsichtung. Wird beim Fortschreiben NIE angefasst. */
    firstSeenAt: string;
    lastSeenAt: string;
    /** Gesetzt, sobald der Nachtlauf den Befund nicht mehr liefert. */
    resolvedAt?: string | null;
    acknowledgedAt?: string | null;
    acknowledgedBy?: string | null;
    acknowledgedByName?: string | null;
    acknowledgedReason?: string | null;
    /**
     * Der Fingerabdruck, der zum Zeitpunkt der Quittierung galt. Weicht er
     * vom aktuellen `fingerprint` ab, ist der Befund wieder aufgelebt: die
     * Abweichung hat sich veraendert, und „gesehen und in Ordnung" gilt fuer
     * diesen Stand nicht mehr.
     */
    acknowledgedFingerprint?: string | null;
}

const TABLE = 'integrity_findings';

const SPALTEN = `
    id, finding_key, project_id, project_number, project_name,
    rule_id, severity, scope_kind, scope_id, scope_label, message,
    expected, actual, details, fingerprint,
    first_seen_at, last_seen_at, resolved_at,
    acknowledged_at, acknowledged_by, acknowledged_by_name,
    acknowledged_reason, acknowledged_fingerprint
`;

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeTabelle = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};

/** `numeric` kann als Zahl ODER als Zeichenkette ankommen — beides zu Zahl. */
const zahl = (v: unknown): number | undefined => {
    if (v === null || v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
};

const text = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);

/**
 * snake_case der Tabelle → camelCase der Typen aus §1.
 *
 * Bewusst eine eigene Abbildung und kein durchgereichtes Rohobjekt: sonst
 * traegt die Oberflaeche die Spaltennamen der Datenbank, und jede
 * Umbenennung in der Migration braecht sie an einer Stelle, an der niemand
 * sie sucht.
 */
const ausZeile = (row: Record<string, unknown>): StoredIntegrityFinding => ({
    id: String(row.id),
    findingKey: String(row.finding_key ?? ''),
    projectId: String(row.project_id ?? ''),
    projectNumber: text(row.project_number),
    projectName: text(row.project_name),
    ruleId: String(row.rule_id ?? ''),
    severity: (row.severity as IntegritySeverity) ?? 'hinweis',
    scopeKind: (row.scope_kind as IntegrityScopeKind) ?? 'projekt',
    scopeId: String(row.scope_id ?? ''),
    scopeLabel: String(row.scope_label ?? ''),
    message: String(row.message ?? ''),
    expected: zahl(row.expected),
    actual: zahl(row.actual),
    details: (row.details as Record<string, unknown> | null) ?? undefined,
    fingerprint: String(row.fingerprint ?? ''),
    firstSeenAt: String(row.first_seen_at ?? ''),
    lastSeenAt: String(row.last_seen_at ?? ''),
    resolvedAt: (row.resolved_at as string | null) ?? null,
    acknowledgedAt: (row.acknowledged_at as string | null) ?? null,
    acknowledgedBy: (row.acknowledged_by as string | null) ?? null,
    acknowledgedByName: (row.acknowledged_by_name as string | null) ?? null,
    acknowledgedReason: (row.acknowledged_reason as string | null) ?? null,
    acknowledgedFingerprint: (row.acknowledged_fingerprint as string | null) ?? null,
});

/**
 * Die Befunde, die dieser Nutzer sehen darf.
 *
 * KEIN Projekt- und kein Rollenfilter in der Abfrage: die SELECT-Policy laesst
 * nur `public.is_admin()` an die Tabelle, und eine Zugriffsregel im Client
 * „zur Sicherheit" nachzubauen hat im Repo schon zwei Wahrheiten fuer
 * dieselbe Frage erzeugt.
 *
 * Vorgabe ist „nur offene": erledigte Zeilen bleiben in der Tabelle stehen
 * (der Nachtlauf setzt nur `resolved_at`), sie sind Archiv und keine
 * Arbeitsliste. QUITTIERTE kommen dagegen IMMER mit — ob sie angezeigt
 * werden, entscheidet der Schalter in der Ansicht, und ein wieder aufgelebter
 * Befund waere sonst nicht zu erkennen, weil dafuer beide Fingerabdruecke
 * nebeneinander liegen muessen.
 */
/**
 * Seitengroesse der Abfrage. Bewusst unter der stillen PostgREST-Grenze von
 * 1000: eine volle Seite ist das Signal, dass es weitergeht.
 */
const SEITE = 500;

/**
 * Befunde zu Projekten herausnehmen, die nicht mehr melden.
 *
 * Beim LESEN und nicht nur beim Erzeugen: der Nachtlauf setzt `resolved_at`
 * erst beim naechsten Durchgang. Bis dahin stuenden Meldungen zu einem
 * Projekt in der Liste, das laengst abgeschlossen und ausgeglichen ist — bei
 * 4959 A waren es sechs. Die gespeicherten Zeilen bleiben unberuehrt; der
 * Nachtlauf raeumt sie regulaer ab.
 */
const ohneStilleProjekte = async (befunde: StoredIntegrityFinding[]): Promise<StoredIntegrityFinding[]> => {
    if (befunde.length === 0) return befunde;
    const still = await ladeStilleProjekte(befunde.map((f) => f.projectId));
    return still.size === 0 ? befunde : befunde.filter((f) => !still.has(f.projectId));
};

/**
 * Befunde zu ÜBUNGSPROJEKTEN herausnehmen (Plan 8.2).
 *
 * Auch hier beim LESEN. Der Nachtlauf laesst Uebungsprojekte zwar schon beim
 * Erzeugen weg, aber zwei Faelle bleiben: Zeilen aus der Zeit VOR dieser
 * Aenderung, und ein Projekt, das erst nach dem letzten Lauf zum
 * Uebungsprojekt wurde. `integrity_findings` fuehrt kein eigenes Kennzeichen,
 * gefiltert wird deshalb ueber die Projekt-ids. Faellt die Abfrage aus, kommt
 * eine leere Menge und es bleibt alles stehen — dieselbe sichere Richtung wie
 * bei `ladeStilleProjekte`.
 */
const ohneUebungsprojekte = async (befunde: StoredIntegrityFinding[]): Promise<StoredIntegrityFinding[]> => {
    if (befunde.length === 0) return befunde;
    const uebung = await ladeUebungsProjektIds();
    return uebung.size === 0 ? befunde : befunde.filter((f) => !uebung.has(f.projectId));
};

export const fetchIntegrityFindings = async (
    optionen: { mitErledigten?: boolean } = {},
): Promise<StoredIntegrityFinding[]> => {
    try {
        // SEITENWEISE, und zwar ausdruecklich. PostgREST kappt eine Antwort
        // still bei 1000 Zeilen — ohne Fehler, ohne Hinweis. In dieser Ansicht
        // waere das die schlimmste Form des Irrtums: die Kopfzahlen und die
        // Projektgruppen saehen vollstaendig aus, und die Befunde ab Zeile
        // 1001 gaebe es fuer den Bearbeiter schlicht nicht. Dieselbe Falle hat
        // im Projekt bereits einmal zugeschlagen.
        const alle: StoredIntegrityFinding[] = [];
        for (let start = 0; ; start += SEITE) {
            let query = supabase
                .from(TABLE)
                .select(SPALTEN)
                .order('last_seen_at', { ascending: false })
                // Zweitschluessel gegen eine wandernde Grenze: bei gleichem
                // `last_seen_at` — nach einem Nachtlauf ist das die Regel —
                // ist die Reihenfolge sonst nicht festgelegt, und eine Zeile
                // kann zwischen zwei Seiten doppelt kommen oder ausfallen.
                .order('finding_key', { ascending: true })
                .range(start, start + SEITE - 1);
            if (!optionen.mitErledigten) query = query.is('resolved_at', null);

            const { data, error } = await query;
            if (error) throw error;
            const seite = data || [];
            alle.push(...seite.map((r) => ausZeile(r as unknown as Record<string, unknown>)));
            if (seite.length < SEITE) return await ohneUebungsprojekte(await ohneStilleProjekte(alle));
        }
    } catch (e) {
        if (istFehlendeTabelle(e)) {
            console.warn(
                'integrity_findings fehlt — supabase/integrity_findings.sql (Nr. 56) noch nicht gefahren.',
            );
            return [];
        }
        // Jeder ANDERE Fehler wird weitergereicht. Eine leere Liste liest
        // sich in der Ansicht als "der letzte Lauf hat nichts gefunden" — bei
        // einem Netz- oder Rechtefehler waere das eine stille Fehlaussage,
        // und zwar ausgerechnet in dem Modul, das stille Fehlaussagen
        // aufdecken soll.
        throw e;
    }
};

/**
 * „Gesehen und in Ordnung" — der einzige Schreibweg des Clients.
 *
 * Ueber die RPC und NICHT per UPDATE auf der Tabelle: fuer Clients gibt es
 * dort ueberhaupt keine UPDATE-Policy (Nr. 56). Ein Direkt-Update aendert
 * unter RLS still null Zeilen und meldet KEINEN Fehler — der Nutzer klickt,
 * der Befund bleibt stehen, und niemand erfaehrt warum.
 *
 * Der Handelnde (`acknowledged_by`) kommt in der RPC aus `auth.uid()` und
 * wird hier bewusst NICHT mitgegeben. Ebenso setzt die RPC
 * `acknowledged_fingerprint` selbst aus der Zeile — haenge der Client den
 * Fingerabdruck an, quittierte er womoeglich einen Stand, der zwischen Laden
 * und Klick schon ueberholt war, und der Deckel laege genau ueber der
 * gewachsenen Luecke.
 *
 * Wirft bei Fehlschlag weiter — der Aufrufer zeigt den Fehler an.
 */
export const acknowledgeIntegrityFinding = async (
    findingId: string,
    begruendung: string,
): Promise<void> => {
    const { error } = await supabase.rpc('acknowledge_integrity_finding', {
        p_finding_id: findingId,
        p_reason: begruendung,
    });
    if (error) throw new Error(describeError(error));
};
